import {
    CONSOLE_READ_ONLY_PATHS,
    CONSOLE_EMPTY_ARRAY_MARKER,
    CONSOLE_REGEX,
    CONSOLE_ANGLE_RANGE,
    CONSOLE_ERROR_MESSAGES,
} from "../../../../../../utils/constants";

/**
 * Flatten nested JSON object/array into flat key-value pairs.
 * Uses __EMPTY_ARRAY__ marker for empty arrays.
 */
export const flattenJson = (obj: unknown, path = ""): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    if (Array.isArray(obj)) {
        if (obj.length === 0) {
            if (path) result[path] = CONSOLE_EMPTY_ARRAY_MARKER;
        } else {
            obj.forEach((item, index) => {
                const newPath = `${path}[${index}]`;
                if (typeof item === "object" && item !== null) {
                    Object.assign(result, flattenJson(item, newPath));
                } else {
                    result[newPath] = item;
                }
            });
        }
    } else if (obj !== null && typeof obj === "object") {
        Object.keys(obj).forEach((key) => {
            const newPath = path ? `${path}.${key}` : key;
            const value = (obj as Record<string, unknown>)[key];
            if (Array.isArray(value) && value.length === 0) {
                result[newPath] = CONSOLE_EMPTY_ARRAY_MARKER;
            } else {
                Object.assign(result, flattenJson(value, newPath));
            }
        });
    } else {
        result[path] = obj;
    }
    return result;
};

/**
 * Unflatten flat key-value object back to nested structure.
 */
export const unflattenJson = (flatObj: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    const arrayMarkers: string[] = [];

    for (const flatKey in flatObj) {
        if (flatObj[flatKey] === CONSOLE_EMPTY_ARRAY_MARKER) {
            arrayMarkers.push(flatKey);
            continue;
        }

        const keys = flatKey.split(CONSOLE_REGEX.PATH_SPLIT).filter((k) => k !== "");
        let current: Record<string, unknown> | unknown[] = result;
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const nextKey = keys[i + 1];
            const isArrayIndex = !isNaN(parseInt(key, 10));

            if (isArrayIndex && !Array.isArray(current)) current = [];
            if ((current as Record<string, unknown>)[key] === undefined) {
                (current as Record<string, unknown>)[key] = isArrayIndex
                    ? i === keys.length - 1
                        ? null
                        : {}
                    : nextKey && !isNaN(parseInt(nextKey, 10))
                      ? []
                      : {};
            }
            if (i === keys.length - 1) {
                (current as Record<string, unknown>)[key] = flatObj[flatKey];
            } else {
                current = (current as Record<string, unknown>)[key] as Record<string, unknown> | unknown[];
            }
        }
    }

    arrayMarkers.forEach((path) => {
        const keys = path.split(CONSOLE_REGEX.PATH_SPLIT).filter((k) => k !== "");
        let current: Record<string, unknown> | unknown[] = result;

        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            const nextKey = keys[i + 1];
            const isArrayIndex = !isNaN(parseInt(nextKey, 10));

            if ((current as Record<string, unknown>)[key] === undefined) {
                (current as Record<string, unknown>)[key] = isArrayIndex ? [] : {};
            }
            current = (current as Record<string, unknown>)[key] as Record<string, unknown> | unknown[];
        }

        const lastKey = keys[keys.length - 1];
        (current as Record<string, unknown>)[lastKey] = [];
    });

    return result;
};

/**
 * Extract display label from flattened path key.
 */
export const getLabelFromKey = (key: string): string => {
    const match = key.match(CONSOLE_REGEX.LABEL_FROM_KEY);
    if (match) return match[1];
    const parts = key.split(".");
    return parts[parts.length - 1];
};

/**
 * Detect input type based on key name and value type.
 */
export const detectInputType = (key: string, value: unknown): string => {
    if (typeof value === "number") return "number";
    if (typeof value === "boolean") return "checkbox";
    if (key.toLowerCase().includes("email")) return "email";
    if (key.toLowerCase().includes("password")) return "password";
    if (key.toLowerCase().includes("date")) return "date";
    return "text";
};

/**
 * Check if path is read-only (exact match or nested under read-only path).
 */
export const isReadOnlyPath = (path: string, isCustomTc = false): boolean => {
    if (isCustomTc) return false;
    const paths = CONSOLE_READ_ONLY_PATHS as readonly string[];
    if (paths.includes(path)) return true;
    for (let i = 0; i < paths.length; i++) {
        const readonlyPath = paths[i];
        if (path.startsWith(`${readonlyPath}.`) || path.startsWith(`${readonlyPath}[`)) {
            return true;
        }
    }
    return false;
};

/**
 * Map exception to user-friendly JSON error message.
 */
export const getJsonErrorFromException = (err: Error): string => {
    if (err.message.startsWith("Read-only field")) return err.message;
    if (err.message === CONSOLE_ERROR_MESSAGES.STRUCTURE_MODIFICATION) return CONSOLE_ERROR_MESSAGES.CANNOT_MODIFY_KEYS;
    if (err.message.includes("value must be between -180 and 180")) return err.message;
    return CONSOLE_ERROR_MESSAGES.INVALID_JSON;
};

interface ValidateJsonOptions {
    originalData?: Record<string, unknown>;
    originalKeys?: Set<string>;
    isCustomTc?: boolean;
}

interface ValidateJsonResult {
    success: boolean;
    error?: string;
    parsed?: Record<string, unknown>;
}

/**
 * Validate JSON string and optionally check structure/read-only constraints.
 */
export const validateJsonAndApply = (
    jsonStr: string,
    options: ValidateJsonOptions = {}
): ValidateJsonResult => {
    const { originalData = {}, originalKeys = new Set(), isCustomTc = false } = options;
    try {
        const parsed = JSON.parse(jsonStr) as Record<string, unknown>;
        const flatParsed = flattenJson(parsed);

        // Only validate that read-only paths haven't been modified
        // Only check if field exists in BOTH original and parsed data
        if (!isCustomTc) {
            for (let i = 0; i < CONSOLE_READ_ONLY_PATHS.length; i++) {
                const path = CONSOLE_READ_ONLY_PATHS[i];
                // Both must exist and be different to trigger error
                if (path in flatParsed && path in originalData && flatParsed[path] !== originalData[path]) {
                    throw new Error(`Read-only field '${path}' cannot be modified`);
                }
            }
        }

        // Allow adding/removing keys - no structure validation needed
        // Users can add new fields freely

        const commandData = parsed?.sequenceData && typeof parsed.sequenceData === "object"
            ? (parsed.sequenceData as Record<string, unknown>).commandData as Record<string, unknown> | undefined
            : undefined;

        const angleMap = commandData
            ? {
                PitchAngle: commandData.PitchAngle,
                RollAngle: commandData.RollAngle,
                YawAngle: commandData.YawAngle,
            }
            : {};

        for (const [key, val] of Object.entries(angleMap)) {
            // Validate angle only if defined and not null
            if (val !== undefined && val !== null) {
                if (typeof val === "number" && (val < CONSOLE_ANGLE_RANGE.MIN || val > CONSOLE_ANGLE_RANGE.MAX)) {
                    throw new Error(`${key} value must be between -180 and 180`);
                }
            }
        }

        return { success: true, parsed };
    } catch (err) {
        return { success: false, error: getJsonErrorFromException(err as Error) };
    }
};
